import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, Pencil, X, Check, Layers, Target, Info, AlertTriangle, Search,
} from "lucide-react";
import { toast }            from "../../../ui/Toast";
import { useConfirmDelete } from "../../../../hooks/useConfirmDelete";

// ─────────────────────────────────────────────────────────────
// Admin > Performance > Profiles.
//
// Two views (BUILD | ASSIGN) over the existing, unchanged backend:
//   GET    /api/admin/performance/profiles                        -> BARE ARRAY
//   POST   /api/admin/performance/profiles                        -> ok(ProfileOut)
//   PATCH  /api/admin/performance/profiles/{id}                   -> ok(ProfileOut)
//   DELETE /api/admin/performance/profiles/{id}                   -> ok({deleted})
//   GET    /api/admin/performance/profiles/{id}/assignments       -> BARE ARRAY
//   POST   /api/admin/performance/profiles/{id}/assignments       -> ok(AssignmentOut)
//   DELETE /api/admin/performance/profiles/{id}/assignments/{aid} -> ok({deleted})
//
// CONTRACT NOTES (verified against the backend, do not "fix" these here):
//  • The two GETs return BARE ARRAYS; every mutation returns an ok()
//    envelope {ok, message, data}. Both shapes are handled below.
//  • Update is PATCH (there is NO PUT route).
//  • PATCH uses exclude_none server-side, so a field can never be set back
//    to null. To CLEAR bell_curve_pct we send {} rather than null.
//  • A profile is a SELECTION of evaluation methods (on/off). The backend
//    stores no per-component weights, so none are collected here — sending
//    them would be silently discarded (Pydantic extra='ignore').
//    bell_curve_pct is the only per-component parameter that persists.
//  • Deleting a profile CASCADES its assignments (FK ON DELETE CASCADE).
// ─────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

const card     = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";
const btnPri   = "text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium";
const btnGhost = "text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors";

// The six values the backend's ProfileMethod enum accepts. These strings ARE
// the contract — "360" (not FEEDBACK_360) and "BELL" are exact.
const EVAL_METHODS = [
  { key: "KRA_KPI", label: "KRA / KPI",   desc: "Weighted goal ratings",          color: "#00ff90" },
  { key: "OKR",     label: "OKR",          desc: "Objectives + key results",       color: "#60a5fa" },
  { key: "360",     label: "360 Feedback", desc: "Peer + subordinate + manager",   color: "#c084fc" },
  { key: "MBO",     label: "MBO",          desc: "Management by objectives",       color: "#fbbf24" },
  { key: "BARS",    label: "BARS",         desc: "Behaviourally anchored ratings", color: "#34d399" },
  { key: "BELL",    label: "Bell Curve",   desc: "Forced distribution overlay",    color: "#fb923c" },
];

// Methods that currently change what a staff member actually sees
// (PerformanceMessengerPage gates these two cards on the resolved profile).
const METHODS_WITH_STAFF_EFFECT = ["KRA_KPI", "360"];

const CYCLE_STAGES = [
  "Goal Setting", "Self Assessment", "Manager Rating",
  "360 Feedback", "Bell Curve", "Calibration", "Final Score", "Increment",
];

const CADENCES = ["Annual", "Bi-annual", "Quarterly", "Monthly"];

// scope_type values are LOWERCASE — the exact strings the backend enum takes.
// `matches` names the StaffProfile field the resolver compares against.
const SCOPE_TYPES = [
  { key: "individual", label: "Individual", matches: "employee code", hint: "The employee's Employee Code on their HR record." },
  { key: "band",       label: "Band",       matches: "band",          hint: "The salary/grade band on the employee's HR record." },
  { key: "department", label: "Department", matches: "department",    hint: "The department on the employee's HR record." },
  { key: "role",       label: "Role",       matches: "role",          hint: "The HR job role (e.g. \"Engineer\"), NOT the system login role." },
];

// Resolution precedence, highest first. LOCKED backend rule — band beats
// department. Mirrored here for the explainer only; nothing client-side
// resolves anything.
const PRECEDENCE = ["individual", "band", "department", "role"];

const EMPTY_PROFILE = {
  name: "", description: "",
  methods: [],
  stages: ["Goal Setting", "Self Assessment", "Manager Rating", "Final Score"],
  cadence: "Annual",
  bell_curve_pct: { low: 15, mid: 70, high: 15 },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// ── Surface the backend's own validation message rather than swallowing it.
// FastAPI 422 -> {detail:[{loc,msg,...}]}; 404/403 -> {detail:"..."}.
async function errorFrom(res) {
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON body */ }
  const d = body?.detail;
  if (Array.isArray(d)) {
    return d.map(e => {
      const field = Array.isArray(e.loc) ? e.loc.filter(p => p !== "body").join(".") : "";
      return field ? `${field}: ${e.msg}` : e.msg;
    }).join(" · ");
  }
  if (typeof d === "string") return d;
  return `Request failed (HTTP ${res.status})`;
}

export default function ProfilesTab() {
  const [view, setView] = useState("build");   // "build" | "assign"

  const [profiles, setProfiles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const { confirmDelete, ConfirmDialog } = useConfirmDelete();

  const loadProfiles = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/admin/performance/profiles`, { headers: authHeaders() });
      if (!res.ok) throw new Error(await errorFrom(res));
      const data = await res.json();
      setProfiles(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  return (
    <div className="space-y-5">
      {/* View switch */}
      <div className="flex gap-2">
        {[
          { key: "build",  label: "Build",  Icon: Layers },
          { key: "assign", label: "Assign", Icon: Target },
        ].map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setView(key)}
            className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border transition-colors ${
              view === key
                ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
            }`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
      )}

      {view === "build" ? (
        <BuildView
          profiles={profiles}
          loading={loading}
          reload={loadProfiles}
          setError={setError}
          confirmDelete={confirmDelete}
        />
      ) : (
        <AssignView
          profiles={profiles}
          loading={loading}
          setError={setError}
          confirmDelete={confirmDelete}
        />
      )}

      {ConfirmDialog}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// BUILD — create / edit / delete profiles
// ═════════════════════════════════════════════════════════════

function BuildView({ profiles, loading, reload, setError, confirmDelete }) {
  const [editing, setEditing] = useState(null);  // null | "new" | profile id
  const [draft,   setDraft]   = useState(EMPTY_PROFILE);
  const [saving,  setSaving]  = useState(false);

  // Real, backend-enforced rules only: name required, >=1 method.
  // (There are no per-component weights in the model, so there is no
  // weight-total rule to mirror.)
  const nameOk    = !!draft.name?.trim();
  const methodsOk = (draft.methods || []).length > 0;
  const canSave   = nameOk && methodsOk && !saving;

  const usesBell    = (draft.methods || []).includes("BELL");
  const bellTotal   = usesBell
    ? ["low", "mid", "high"].reduce((s, k) => s + Number(draft.bell_curve_pct?.[k] ?? 0), 0)
    : null;
  // Advisory only — the backend does NOT validate this sum, so it never
  // blocks Save. Flagged so an admin isn't left with a silent 87% curve.
  const bellOff = usesBell && bellTotal !== 100;

  const staffVisible = (draft.methods || []).filter(m => METHODS_WITH_STAFF_EFFECT.includes(m));

  const openNew  = () => { setDraft({ ...EMPTY_PROFILE }); setEditing("new"); };
  const openEdit = (p) => {
    setDraft({
      ...EMPTY_PROFILE,
      ...p,
      methods: [...(p.methods || [])],
      stages:  [...(p.stages  || [])],
      bell_curve_pct: p.bell_curve_pct || { low: 15, mid: 70, high: 15 },
    });
    setEditing(p.id);
  };

  const toggleMethod = (key) => setDraft(d => ({
    ...d,
    methods: d.methods.includes(key) ? d.methods.filter(m => m !== key) : [...d.methods, key],
  }));

  const toggleStage = (stage) => setDraft(d => {
    if (d.stages.includes(stage)) return { ...d, stages: d.stages.filter(s => s !== stage) };
    // Keep the canonical CYCLE_STAGES order rather than click order, so the
    // stored flow reads correctly wherever it's displayed.
    const next = [...d.stages, stage];
    return { ...d, stages: CYCLE_STAGES.filter(s => next.includes(s)) };
  });

  const save = async () => {
    setSaving(true); setError(null);
    const isNew = editing === "new";
    const payload = {
      name:        draft.name.trim(),
      description: draft.description || "",
      methods:     draft.methods,
      stages:      draft.stages,
      cadence:     draft.cadence || "Annual",
      // PATCH can't null a field server-side (exclude_none), so {} is how a
      // previously-set curve gets cleared when BELL is switched off.
      bell_curve_pct: usesBell ? draft.bell_curve_pct : (isNew ? undefined : {}),
    };
    try {
      const res = await fetch(
        isNew
          ? `${API}/api/admin/performance/profiles`
          : `${API}/api/admin/performance/profiles/${editing}`,
        { method: isNew ? "POST" : "PATCH", headers: authHeaders(), body: JSON.stringify(payload) },
      );
      if (!res.ok) throw new Error(await errorFrom(res));
      await reload();
      setEditing(null);
      toast.success(isNew ? "Profile created" : "Profile updated");
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = (p) => confirmDelete(
    `the profile "${p.name}" and all of its assignments`,
    async () => {
      try {
        const res = await fetch(`${API}/api/admin/performance/profiles/${p.id}`, {
          method: "DELETE", headers: authHeaders(),
        });
        if (!res.ok) throw new Error(await errorFrom(res));
        if (editing === p.id) setEditing(null);
        await reload();
        toast.success("Profile deleted");
      } catch (e) {
        setError(e.message);
        toast.error(e.message);
      }
    },
  );

  return (
    <div className="space-y-5">
      {/* ── Builder ── */}
      {editing && (
        <div className={`${card} p-5 space-y-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {editing === "new" ? "New Performance Profile" : `Edit — ${draft.name}`}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Profile Name *</label>
              <input className={inputCls} value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Engineering Track" />
            </div>
            <div>
              <label className={labelCls}>Cadence</label>
              <select className={inputCls} value={draft.cadence}
                onChange={e => setDraft(d => ({ ...d, cadence: e.target.value }))}>
                {CADENCES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Description</label>
              <input className={inputCls} value={draft.description || ""}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                placeholder="Brief description of this profile" />
            </div>
          </div>

          {/* Components — on/off. The backend stores a method SELECTION; it
              has no weight field, so no weights are collected. */}
          <div>
            <label className={labelCls}>Evaluation Components *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {EVAL_METHODS.map(({ key, label, desc, color }) => {
                const active = draft.methods.includes(key);
                const inert  = !METHODS_WITH_STAFF_EFFECT.includes(key);
                return (
                  <button key={key} onClick={() => toggleMethod(key)} type="button"
                    aria-pressed={active}
                    className={`text-left rounded-xl border p-3 transition-all ${
                      active ? "" : "border-white/5 bg-white/3 hover:border-white/15"
                    }`}
                    style={active ? { borderColor: `${color}50`, background: `${color}10` } : {}}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-sm font-medium"
                        style={{ color: active ? color : "rgba(255,255,255,0.5)" }}>{label}</span>
                      {active && <Check size={12} style={{ color }} />}
                    </div>
                    <p className="text-xs text-white/25">{desc}</p>
                    {active && inert && (
                      <p className="text-xs text-white/20 mt-1.5 italic">Recorded — no staff-facing form yet</p>
                    )}
                  </button>
                );
              })}
            </div>
            {!methodsOk && (
              <p className="text-xs text-amber-300/70 mt-2">Select at least one component — the backend rejects an empty set.</p>
            )}
          </div>

          {/* Bell curve — the one per-component parameter that persists. */}
          {usesBell && (
            <div>
              <label className={labelCls}>Bell Curve Distribution Targets (%)</label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[["low", "Low (bottom %)"], ["mid", "Mid (core %)"], ["high", "High (top %)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-white/30 block mb-1">{l}</label>
                    <input type="number" className={inputCls}
                      value={draft.bell_curve_pct?.[k] ?? 0}
                      onChange={e => setDraft(d => ({
                        ...d,
                        bell_curve_pct: { ...d.bell_curve_pct, [k]: Number(e.target.value) },
                      }))} />
                  </div>
                ))}
              </div>
              <p className={`text-xs mt-2 ${bellOff ? "text-amber-300/80" : "text-white/25"}`}>
                Total: {bellTotal}%{bellOff ? " — most curves total 100%. Saved either way (not enforced by the backend)." : ""}
              </p>
            </div>
          )}

          {/* Stages */}
          <div>
            <label className={labelCls}>Cycle Stages</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {CYCLE_STAGES.map(stage => {
                const active = draft.stages.includes(stage);
                return (
                  <button key={stage} onClick={() => toggleStage(stage)} type="button"
                    aria-pressed={active}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]"
                        : "border-white/10 bg-white/5 text-white/40 hover:border-white/20 hover:text-white/60"
                    }`}>
                    {stage}
                  </button>
                );
              })}
            </div>
            {draft.stages.length > 0 && (
              <p className="text-xs text-white/25 mt-2">Flow: {draft.stages.join(" → ")}</p>
            )}
          </div>

          {/* What this profile actually does to the staff view. */}
          <div className="flex items-start gap-2 rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 p-3">
            <Info size={13} className="text-[#00ff90]/60 shrink-0 mt-0.5" />
            <p className="text-xs text-white/40 leading-relaxed">
              {staffVisible.length === 0 ? (
                <>Employees on this profile will see <span className="text-amber-300/80">no goal tracker and no peer feedback</span> —
                  neither KRA/KPI nor 360 is selected. Their summary, self review and history still show.</>
              ) : (
                <>Employees on this profile will see:{" "}
                  <span className="text-[#00ff90]/70">
                    {staffVisible.map(m => EVAL_METHODS.find(x => x.key === m)?.label).join(" + ")}
                  </span>
                  , plus summary, self review and history.</>
              )}
            </p>
          </div>

          <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
            <button onClick={() => setEditing(null)} className={btnGhost}>Cancel</button>
            <button onClick={save} disabled={!canSave} className={btnPri}>
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      )}

      {/* ── Profile list ── */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={13} className="text-[#00ff90]/60" />
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance Profiles</span>
          </div>
          <button onClick={openNew}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
            <Plus size={12} /> New Profile
          </button>
        </div>

        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>
            ))
          ) : profiles.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No profiles configured. Create one to get started.</p>
          ) : profiles.map(p => (
            <div key={p.id} className="flex items-start justify-between gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm text-white/80 font-medium">{p.name}</p>
                  <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{p.cadence}</span>
                </div>
                {p.description && <p className="text-xs text-white/30 mb-2">{p.description}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {(p.methods || []).map(m => {
                    const cfg = EVAL_METHODS.find(x => x.key === m);
                    return (
                      <span key={m} className="text-xs px-2 py-0.5 rounded-full border"
                        style={{
                          color: cfg?.color || "#ffffff44",
                          borderColor: `${cfg?.color || "#ffffff33"}40`,
                          background: `${cfg?.color || "#ffffff11"}15`,
                        }}>
                        {cfg?.label || m}
                      </span>
                    );
                  })}
                  {p.bell_curve_pct && Object.keys(p.bell_curve_pct).length > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full border border-white/10 text-white/35">
                      {["low", "mid", "high"].map(k => p.bell_curve_pct[k] ?? 0).join(" / ")}
                    </span>
                  )}
                </div>
                {p.stages?.length > 0 && (
                  <p className="text-xs text-white/20 mt-1.5">{p.stages.join(" → ")}</p>
                )}
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => openEdit(p)} aria-label={`Edit ${p.name}`}
                  className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                  <Pencil size={12} />
                </button>
                <button onClick={() => del(p)} aria-label={`Delete ${p.name}`}
                  className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// ASSIGN — bind profiles to a role / dept / band / individual
// ═════════════════════════════════════════════════════════════

function AssignView({ profiles, loading, setError, confirmDelete }) {
  const [rows,        setRows]        = useState([]);   // flattened {profile, assignment}
  const [rowsLoading, setRowsLoading] = useState(false);
  const [saving,      setSaving]      = useState(false);

  const [form, setForm] = useState({
    profile_id: "", scope_type: "department", scope_value: "", priority: 1,
  });

  // Real department values, derived from the org chart (the only bulk source
  // in the API that exposes StaffProfile.department).
  const [departments, setDepartments] = useState([]);
  const [people,      setPeople]      = useState([]);
  const [lookupWarn,  setLookupWarn]  = useState(false);
  const [personQuery, setPersonQuery] = useState("");

  // Assignments are only listable per-profile, so fan out across profiles and
  // flatten. N is the profile count (small); this is what makes the combined
  // precedence table possible.
  const loadAssignments = useCallback(async () => {
    if (!profiles.length) { setRows([]); return; }
    setRowsLoading(true);
    try {
      const results = await Promise.all(profiles.map(async (p) => {
        const res = await fetch(`${API}/api/admin/performance/profiles/${p.id}/assignments`, { headers: authHeaders() });
        if (!res.ok) return [];
        const data = await res.json();
        return (Array.isArray(data) ? data : []).map(a => ({ profile: p, assignment: a }));
      }));
      setRows(results.flat());
    } catch (e) {
      setError(e.message);
    } finally {
      setRowsLoading(false);
    }
  }, [profiles, setError]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  // Org chart -> distinct departments + a people list for the individual picker.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/admin/org-chart`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled || !d) { if (!cancelled) setLookupWarn(true); return; }
        const flat = [];
        const walk = (nodes) => (nodes || []).forEach(n => { flat.push(n); walk(n.reports); });
        walk(d.tree);
        const depts = [...new Set(flat.map(n => n.department).filter(x => x && x !== "—"))].sort();
        setDepartments(depts);
        setPeople(flat.map(n => ({ id: n.id, name: n.name, email: n.email, department: n.department })));
      })
      .catch(() => { if (!cancelled) setLookupWarn(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!form.profile_id && profiles.length) setForm(f => ({ ...f, profile_id: profiles[0].id }));
  }, [profiles, form.profile_id]);

  const scopeCfg = SCOPE_TYPES.find(s => s.key === form.scope_type) || SCOPE_TYPES[2];

  // Client-side mirror of the real backend rules: scope_value non-empty,
  // scope_type in the enum, a profile chosen.
  const canAssign = !!form.profile_id && !!form.scope_value.trim() && !saving;

  // Duplicate warning — the backend does NOT prevent this; the winner is then
  // decided silently by priority/created_at/id.
  const duplicate = useMemo(() => rows.find(r =>
    r.assignment.scope_type === form.scope_type &&
    (r.assignment.scope_value || "").trim().toLowerCase() === form.scope_value.trim().toLowerCase()
  ), [rows, form.scope_type, form.scope_value]);

  const filteredPeople = useMemo(() => {
    const q = personQuery.trim().toLowerCase();
    if (!q) return [];
    return people.filter(p =>
      (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [people, personQuery]);

  const assign = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetch(`${API}/api/admin/performance/profiles/${form.profile_id}/assignments`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          scope_type:  form.scope_type,
          scope_value: form.scope_value.trim(),
          priority:    Number(form.priority) || 1,
        }),
      });
      if (!res.ok) throw new Error(await errorFrom(res));
      setForm(f => ({ ...f, scope_value: "" }));
      setPersonQuery("");
      await loadAssignments();
      toast.success("Profile assigned");
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const unassign = (row) => confirmDelete(
    `the ${row.assignment.scope_type} assignment "${row.assignment.scope_value}" from ${row.profile.name}`,
    async () => {
      try {
        const res = await fetch(
          `${API}/api/admin/performance/profiles/${row.profile.id}/assignments/${row.assignment.id}`,
          { method: "DELETE", headers: authHeaders() },
        );
        if (!res.ok) throw new Error(await errorFrom(res));
        await loadAssignments();
        toast.success("Assignment removed");
      } catch (e) {
        setError(e.message);
        toast.error(e.message);
      }
    },
  );

  const sortedRows = useMemo(() => {
    const rank = (t) => { const i = PRECEDENCE.indexOf(t); return i === -1 ? 99 : i; };
    return [...rows].sort((a, b) =>
      rank(a.assignment.scope_type) - rank(b.assignment.scope_type) ||
      (a.assignment.priority ?? 0) - (b.assignment.priority ?? 0) ||
      String(a.assignment.scope_value).localeCompare(String(b.assignment.scope_value))
    );
  }, [rows]);

  return (
    <div className="space-y-5">
      {/* ── Precedence explainer ── */}
      <div className={`${card} p-5`}>
        <div className="flex items-center gap-2 mb-3">
          <Info size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Conflict Resolution</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {PRECEDENCE.map((k, i) => {
            const cfg = SCOPE_TYPES.find(s => s.key === k);
            return (
              <React.Fragment key={k}>
                <span className="text-xs px-3 py-1.5 rounded-full border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/80">
                  {cfg.label}
                </span>
                {i < PRECEDENCE.length - 1 && <span className="text-white/25 text-xs">beats</span>}
              </React.Fragment>
            );
          })}
        </div>
        <p className="text-xs text-white/35 leading-relaxed">
          When more than one assignment matches an employee, the highest-precedence scope wins outright —
          lower scopes are never consulted. Note that <span className="text-white/60">Band beats Department</span>.
          Within one scope, the tiebreak is: <span className="text-white/60">lowest priority number</span>, then
          earliest created, then lowest id. An employee matching nothing gets no profile and sees every card
          (the safe default).
        </p>
      </div>

      {/* ── Assign form ── */}
      <div className={`${card} p-5 space-y-4`}>
        <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Assign a Profile</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Profile</label>
            <select className={inputCls} value={form.profile_id}
              onChange={e => setForm(f => ({ ...f, profile_id: e.target.value }))}>
              {profiles.length === 0 && <option value="">No profiles yet</option>}
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>Assign To</label>
            <select className={inputCls} value={form.scope_type}
              onChange={e => setForm(f => ({ ...f, scope_type: e.target.value, scope_value: "" }))}>
              {SCOPE_TYPES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>

          {/* Target picker switches on scope type. */}
          <div className="md:col-span-2">
            <label className={labelCls}>{scopeCfg.label} — matches {scopeCfg.matches}</label>

            {form.scope_type === "department" && departments.length > 0 ? (
              <select className={inputCls} value={form.scope_value}
                onChange={e => setForm(f => ({ ...f, scope_value: e.target.value }))}>
                <option value="">Select department…</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : form.scope_type === "individual" ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
                  <input className={`${inputCls} pl-8`} value={personQuery}
                    onChange={e => setPersonQuery(e.target.value)}
                    placeholder="Search a person to look up their details…" />
                </div>
                {filteredPeople.length > 0 && (
                  <div className="rounded-lg border border-white/10 bg-black divide-y divide-white/5">
                    {filteredPeople.map(p => (
                      <button key={p.id} type="button" onClick={() => setPersonQuery(p.name)}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 transition-colors">
                        <p className="text-xs text-white/70">{p.name}</p>
                        <p className="text-xs text-white/25">{p.email}{p.department && p.department !== "—" ? ` · ${p.department}` : ""}</p>
                      </button>
                    ))}
                  </div>
                )}
                <input className={inputCls} value={form.scope_value}
                  onChange={e => setForm(f => ({ ...f, scope_value: e.target.value }))}
                  placeholder="Employee Code (e.g. E001)" />
              </div>
            ) : (
              <input className={inputCls} value={form.scope_value}
                onChange={e => setForm(f => ({ ...f, scope_value: e.target.value }))}
                placeholder={
                  form.scope_type === "band" ? "e.g. L4" :
                  form.scope_type === "role" ? "e.g. Engineer" : "Value"
                } />
            )}
            <p className="text-xs text-white/25 mt-1.5">{scopeCfg.hint} Matching is case-insensitive and trimmed, but otherwise exact.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className={labelCls}>Priority</label>
            <input type="number" className={inputCls} value={form.priority}
              onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} />
            <p className="text-xs text-white/25 mt-1.5">Lower number wins ties within the same scope.</p>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button onClick={assign} disabled={!canAssign} className={btnPri}>
              {saving ? "Assigning…" : "Add Assignment"}
            </button>
          </div>
        </div>

        {duplicate && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 p-3">
            <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/80">
              <span className="font-medium">{duplicate.profile.name}</span> already claims this {form.scope_type}.
              Adding it again is allowed, but the winner is then decided silently by priority, then creation order.
            </p>
          </div>
        )}

        {/* Only `department` has a bulk lookup source in the API today. */}
        {(form.scope_type !== "department" || lookupWarn) && (
          <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/3 p-3">
            <AlertTriangle size={13} className="text-white/30 shrink-0 mt-0.5" />
            <p className="text-xs text-white/35 leading-relaxed">
              {form.scope_type === "department" && lookupWarn
                ? "Department list unavailable — enter the value exactly as it appears on the HR record."
                : <>There is no lookup endpoint for {scopeCfg.matches} yet, so this value is typed rather than
                   picked. A value that doesn't match any employee's HR record simply never resolves —
                   no error is raised. Check the spelling against the employee's profile.</>}
            </p>
          </div>
        )}
      </div>

      {/* ── Current assignments ── */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target size={13} className="text-[#00ff90]/60" />
            <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Current Assignments</span>
          </div>
          <span className="text-xs text-white/30">{rows.length} total</span>
        </div>

        <div className="overflow-x-auto">
          {loading || rowsLoading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 rounded-lg bg-white/5 animate-pulse" />)}
            </div>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">
              No assignments yet. Employees with no matching assignment see all cards.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  {["Scope", "Value", "Profile", "Priority", ""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(row => (
                  <tr key={row.assignment.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/8 text-[#00ff90]/70 capitalize">
                        {row.assignment.scope_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70 text-xs">{row.assignment.scope_value}</td>
                    <td className="px-4 py-3 text-white/50 text-xs">{row.profile.name}</td>
                    <td className="px-4 py-3 text-white/30 text-xs">{row.assignment.priority}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => unassign(row)}
                        aria-label={`Remove ${row.assignment.scope_type} assignment ${row.assignment.scope_value}`}
                        className="text-xs px-3 py-1 rounded-lg border border-white/10 text-white/30 hover:text-red-400 hover:border-red-400/30 transition-colors">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
